import { css } from 'lit';
import { LaneBlockage, RecordStatus } from '@typings/api';
import { ConfigREMEventForm } from '@config/ConfigREM';
import { FrameNewPageRegex, LOCALE } from '@/constants';
import { isLaneImpacted, isValidString, numberIsValidAndFinite } from '@/utils/utils';

/**	Field names whose values are epoch milliseconds rather than plain numbers.	*/
const TimestampFieldNames = new Set([
	'arrived',
	'assigned',
	'completed',
	'eventEnd',
	'eventStart',
	'verified',
]);

/**	Field names whose values are mile markers and should keep one decimal place.	*/
const MileFieldNames = new Set(['mileEnd', 'mileStart']);

export const formatTimelineDate = (date: number | Date): string =>
	new Date(date).toLocaleString(LOCALE, ConfigREMEventForm.timelineDateFormatOptions);

export const formatTimelineDateAndTime = (date: number | Date): string =>
	new Date(date).toLocaleString(LOCALE, ConfigREMEventForm.wholeDatestampFormatOptions);

/**	Keeps the app's existing 12-hour, timezone-suffixed format; the <sup> wrapping is the view's job.	*/
export const formatTimelineTime = (date: number | Date): string =>
	new Date(date).toLocaleTimeString(LOCALE, ConfigREMEventForm.timelineTimeFormatOptions);

/**	Turns backend shouting ("ABANDONED VEHICLE") into prose for row titles.	*/
export const sentenceCase = (text: string): string => {
	if (!isValidString(text) || text !== text.toUpperCase()) {
		return text;
	}

	return text.charAt(0) + text.slice(1).toLowerCase();
};

const namedValueToString = (value: unknown): string | undefined => {
	if (value == null) {
		return undefined;
	}
	if (typeof value === 'object') {
		const { name } = value as { name?: unknown };

		return isValidString(name) ? name : undefined;
	}

	return String(value);
};

/**
 * Best-effort conversion of a ChangeSetItem value into display text.
 *
 * Returns undefined when the value is absent or empty, which callers treat as a clear/removal
 * rather than printing "undefined" the way the previous string formatters could.
 */
export const formatChangeValue = (fieldName: string, value: unknown): string | undefined => {
	if (value == null || value === '') {
		return undefined;
	}

	if (Array.isArray(value)) {
		const names = value.map(namedValueToString).filter(isValidString);

		return names.length > 0 ? names.join(', ') : undefined;
	}

	if (TimestampFieldNames.has(fieldName)) {
		return numberIsValidAndFinite(value) ? formatTimelineDateAndTime(value) : undefined;
	}

	if (MileFieldNames.has(fieldName)) {
		return numberIsValidAndFinite(value) ? value.toFixed(1) : undefined;
	}

	if (fieldName === 'recordStatus') {
		return numberIsValidAndFinite(value) ? RecordStatus[value] : namedValueToString(value);
	}

	if (typeof value === 'boolean') {
		return value ? 'Yes' : 'No';
	}

	return namedValueToString(value);
};

/**
 * Lane numbers arrive either as a bare array or wrapped in a `values` collection depending on the
 * endpoint, so normalise both into sorted lane numbers.
 */
const toLaneNumbers = (lanesAffected: LaneBlockage['lanesAffected']): number[] => {
	return (lanesAffected ?? []).filter(numberIsValidAndFinite).sort((laneA, laneB) => laneA - laneB);
};

/**	Wording used when a direction has nothing blocked.	*/
export const NoLaneImpact = 'No lanes impacted';

/**	Describes which parts of a direction's lane blockage changed, in operator wording.	*/
export const laneBlockageToText = (
	laneBlockage?: LaneBlockage,
	oldLaneBlockage?: LaneBlockage,
): string => {
	if (isLaneImpacted(laneBlockage) && laneBlockage) {
		const lanesAffected = toLaneNumbers(laneBlockage.lanesAffected);
		const numLanes = lanesAffected.length;
		const impactMessages: string[] = [];

		if (numLanes > 0) {
			const lanes =
				numLanes > 1
					? `${lanesAffected.slice(0, numLanes - 1).join(', ')} and ${lanesAffected[numLanes - 1]}`
					: `${lanesAffected[0]}`;

			impactMessages.push(`Lane${numLanes > 1 ? 's' : ''} ${lanes}`);
		}
		if (laneBlockage.insideShoulderAffected) {
			impactMessages.push('inside shoulder');
		}
		if (laneBlockage.outsideShoulderAffected) {
			impactMessages.push('outside shoulder');
		}
		if (laneBlockage.entranceRampAffected) {
			impactMessages.push('entrance ramp');
		}
		if (laneBlockage.exitRampAffected) {
			impactMessages.push('exit ramp');
		}
		if (laneBlockage.allLanesAffected && numLanes === 0) {
			impactMessages.push('Affected direction added');
		}
		if (oldLaneBlockage?.allLanesAffected && laneBlockage.allLanesAffected === false) {
			impactMessages.push('Affected direction removed');
		}

		return impactMessages.join(', ');
	}
	if (oldLaneBlockage?.allLanesAffected && laneBlockage?.allLanesAffected === false) {
		return 'Affected direction removed';
	}

	return NoLaneImpact;
};

const SignMessageActionRegex = /^\s*(Added|Removed)\s*:\s*/;

export type ParsedSignMessage = {
	action?: 'Added' | 'Removed';
	pages: string[];
};

/**
 * Parses a `signs-display` value, which arrives as "Added: <message>" where the message may
 * contain [np] page breaks.
 */
export const parseDisplayedSignMessage = (value: unknown): ParsedSignMessage => {
	if (!isValidString(value)) {
		return { pages: [] };
	}

	const action = SignMessageActionRegex.exec(value)?.[1] as 'Added' | 'Removed' | undefined;
	const pages = value
		.replace(SignMessageActionRegex, '')
		.split(FrameNewPageRegex)
		.map((page) => page.trim())
		.filter(isValidString);

	return { action, pages };
};

/**
 * Parses a `messages` value, which arrives as "<sign name>: <message>". Colons inside the message
 * itself are ambiguous, so the last segment wins, matching the long-standing behaviour.
 */
export const parseEditedSignMessage = (value: unknown): { signName?: string; pages: string[] } => {
	if (!isValidString(value)) {
		return { pages: [] };
	}

	const segments = value.split(':');
	const message = segments.pop()?.trim() ?? '';
	const signName = segments.pop()?.trim();

	return {
		signName: isValidString(signName) ? signName : undefined,
		pages: message
			.split(FrameNewPageRegex)
			.map((page) => page.trim())
			.filter(isValidString),
	};
};
